/**
 * Per-lesson analytics — watch hours, completion, and where learners drop off.
 * Companion to the dashboard's top-10 ranking (US-A10 / FR-ADM-12), scoped to
 * one content item so operators can diagnose a specific lesson.
 */
import { useQuery } from '@tanstack/react-query'
import { Card, Col, Empty, Row, Segmented, Skeleton, Space, Statistic, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { supabaseClient } from '@/lib/supabase'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'

type Period = '7d' | '30d'

interface Summary {
  watch_hours: number
  learners: number
  avg_session_min: number
  completion_rate: number
}

interface TrendPoint {
  day: string
  watch_hours: number
}

interface DropoffBucket {
  bucket: number
  learners: number
}

const asNumber = (value: number | string | null | undefined) => Number(value ?? 0)

const decimal = (value: number, locale: string) =>
  new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'fr-FR', { maximumFractionDigits: 1 }).format(
    value,
  )

export const ContentAnalyticsPanel = ({ contentId }: { contentId: string }) => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const [period, setPeriod] = useState<Period>('30d')
  const days = period === '7d' ? 7 : 30
  const to = dayjs().endOf('day').add(1, 'millisecond')
  const from = to.subtract(days, 'day').startOf('day')

  const analyticsQuery = useQuery({
    queryKey: ['content-analytics', contentId, period],
    queryFn: async () => {
      const params = { p_content_id: contentId, p_from: from.toISOString(), p_to: to.toISOString() }
      const [summaryResult, trendResult, dropoffResult] = await Promise.all([
        supabaseClient.rpc('admin_content_analytics_summary', params),
        supabaseClient.rpc('admin_content_analytics_trend', params),
        supabaseClient.rpc('admin_content_dropoff', { p_content_id: contentId }),
      ])
      const error = summaryResult.error ?? trendResult.error ?? dropoffResult.error
      if (error) throw error
      return {
        summary: ((summaryResult.data ?? [])[0] ?? null) as Summary | null,
        trend: (trendResult.data ?? []) as TrendPoint[],
        dropoff: (dropoffResult.data ?? []) as DropoffBucket[],
      }
    },
  })

  const summary = analyticsQuery.data?.summary
  const trend = analyticsQuery.data?.trend ?? []
  const dropoff = analyticsQuery.data?.dropoff ?? []
  const loading = analyticsQuery.isLoading

  const dropoffByBucket = new Map(dropoff.map(item => [item.bucket, item.learners]))
  const dropoffSeries = Array.from({ length: 10 }, (_, index) => index * 10).map(bucket => ({
    bucket,
    learners: dropoffByBucket.get(bucket) ?? 0,
  }))

  return (
    <Card
      className="dashboard-chart-card"
      title={translate('admin.screens.contentAnalytics.title')}
      extra={
        <Segmented<Period>
          value={period}
          onChange={setPeriod}
          options={[
            { value: '7d', label: translate('admin.screens.dashboard.period7') },
            { value: '30d', label: translate('admin.screens.dashboard.period30') },
          ]}
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} lg={6}>
            <Statistic
              title={translate('admin.screens.contentAnalytics.watchHours')}
              value={loading ? undefined : decimal(asNumber(summary?.watch_hours), locale)}
            />
          </Col>
          <Col xs={12} lg={6}>
            <Statistic
              title={translate('admin.screens.contentAnalytics.learners')}
              value={loading ? undefined : asNumber(summary?.learners)}
            />
          </Col>
          <Col xs={12} lg={6}>
            <Statistic
              title={translate('admin.screens.contentAnalytics.avgSession')}
              value={loading ? undefined : decimal(asNumber(summary?.avg_session_min), locale)}
              suffix={translate('admin.screens.contentAnalytics.minutesSuffix')}
            />
          </Col>
          <Col xs={12} lg={6}>
            <Statistic
              title={translate('admin.screens.contentAnalytics.completionRate')}
              value={loading ? undefined : decimal(asNumber(summary?.completion_rate), locale)}
              suffix="%"
            />
          </Col>
        </Row>

        <div>
          <Typography.Text strong>
            {translate('admin.screens.contentAnalytics.trendTitle')}
          </Typography.Text>
          {loading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : trend.some(point => point.watch_hours > 0) ? (
            <div className="dashboard-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="content-analytics-watch-hours" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--admin-primary)" stopOpacity={0.24} />
                      <stop offset="95%" stopColor="var(--admin-primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="var(--admin-border)"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    axisLine={false}
                    dataKey="day"
                    tickFormatter={value => dayjs(value).format('DD/MM')}
                    tickLine={false}
                    tick={{ fill: 'var(--admin-text-secondary)', fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickFormatter={value => decimal(Number(value), locale)}
                    tickLine={false}
                    tick={{ fill: 'var(--admin-text-secondary)', fontSize: 12 }}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => decimal(Number(value), locale)}
                    labelFormatter={label => dayjs(label).format('dddd D MMMM')}
                    contentStyle={{ borderColor: 'var(--admin-border)', borderRadius: 12 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="watch_hours"
                    name={translate('admin.screens.dashboard.watchHoursSeries')}
                    stroke="var(--admin-primary)"
                    strokeWidth={2.5}
                    fill="url(#content-analytics-watch-hours)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={translate('admin.screens.dashboard.noActivity')}
            />
          )}
        </div>

        <div>
          <Typography.Text strong>
            {translate('admin.screens.contentAnalytics.dropoffTitle')}
          </Typography.Text>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {translate('admin.screens.contentAnalytics.dropoffBody')}
          </Typography.Paragraph>
          {loading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : dropoffSeries.some(point => point.learners > 0) ? (
            <div className="dashboard-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dropoffSeries} margin={{ top: 10, bottom: 0 }}>
                  <CartesianGrid
                    vertical={false}
                    stroke="var(--admin-border)"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    axisLine={false}
                    dataKey="bucket"
                    tickFormatter={value => `${value}%`}
                    tickLine={false}
                    tick={{ fill: 'var(--admin-text-secondary)', fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--admin-text-secondary)', fontSize: 12 }}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => value}
                    labelFormatter={value =>
                      translate('admin.screens.contentAnalytics.dropoffBucketLabel', {
                        low: value,
                        high: Number(value) + 9,
                      })
                    }
                    contentStyle={{ borderColor: 'var(--admin-border)', borderRadius: 12 }}
                  />
                  <Bar
                    dataKey="learners"
                    name={translate('admin.screens.contentAnalytics.learners')}
                    fill="var(--admin-primary)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={translate('admin.screens.contentAnalytics.noProgress')}
            />
          )}
        </div>
      </Space>
    </Card>
  )
}
